"use client";

import Link from "next/link";
import { ArrowRight, BookOpen } from "lucide-react";
import {
  CrossSellBand,
  DevLandingShell,
  FinalCta,
  GetKeyButton,
  HeroBadge,
  LiveDot,
  SecondaryCta,
  SectionHeading,
  StatsRow,
  StepsCard,
  metalText,
} from "@/components/marketing/dev-landing/chrome";
import { FeatureSplit, TextFeatureList } from "@/components/marketing/dev-landing/sections";
import { GexEventsMock, GexHeatmapMock, GexProfileMock } from "@/components/marketing/dev-landing/mocks";
import { CodeWindow, K, Line, N, P, S } from "@/components/marketing/dev-landing/code-window";
import { EndpointTable } from "@/components/marketing/dev-landing/endpoint-table";

const q = (v: string) => `"${v}"`;

export default function GexApiLandingClient({ keyHref }: { keyHref: string }) {
  return (
    <DevLandingShell>
      {/* Hero */}
      <div className="text-center">
        <HeroBadge>
          <LiveDot />
          <span className="text-sm text-[#d4d5d5]">Real-time gamma exposure &middot; any ticker</span>
        </HeroBadge>

        <h1 className="mx-auto max-w-4xl text-balance text-4xl tracking-tight text-white sm:text-5xl md:text-6xl">
          The real-time <span style={metalText}>gamma exposure</span> API
        </h1>

        <p className="mx-auto mt-6 max-w-2xl text-balance text-lg leading-relaxed text-white/70 sm:text-xl">
          Call walls, put walls, the gamma flip and net GEX per strike - computed live from the full
          options chain and served as JSON your code can act on.
        </p>

        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <GetKeyButton keyHref={keyHref} eventName="gex_api_lp_get_key_clicked" />
          <SecondaryCta href="/docs">
            <BookOpen className="h-4 w-4" />
            Read the docs
          </SecondaryCta>
        </div>
        <p className="mt-4 text-xs text-white/50 sm:text-sm">Included with every paid QuantWheel plan.</p>
      </div>

      {/* Hero demo: response + rendered profile */}
      <div className="relative mx-auto mt-12 max-w-5xl md:mt-16">
        <div
          className="pointer-events-none absolute -inset-12 rounded-full opacity-20"
          style={{
            background: "radial-gradient(ellipse, rgba(16,185,129,0.3) 0%, rgba(154,160,158,0.08) 45%, transparent 70%)",
          }}
          aria-hidden="true"
        />
        <div className="relative grid gap-5 md:grid-cols-2">
          <CodeWindow title="/api/v1/gex?symbol=SPY" method="GET" status="200 - 38ms">
            <Line>
              <P>{"{"}</P>
            </Line>
            <Line indent={1}>
              <K>{q("symbol")}</K>
              <P>: </P>
              <S>{q("SPY")}</S>
              <P>,</P>
            </Line>
            <Line indent={1}>
              <K>{q("stockPrice")}</K>
              <P>: </P>
              <N>622.41</N>
              <P>,</P>
            </Line>
            <Line indent={1}>
              <K>{q("gammaZone")}</K>
              <P>: </P>
              <S>{q("negative")}</S>
              <P>,</P>
            </Line>
            <Line indent={1}>
              <K>{q("gammaInflection")}</K>
              <P>: </P>
              <N>620.5</N>
              <P>,</P>
            </Line>
            <Line indent={1}>
              <K>{q("callWall")}</K>
              <P>: {"{ "}</P>
              <K>{q("strike")}</K>
              <P>: </P>
              <N>630</N>
              <P>, </P>
              <K>{q("gex")}</K>
              <P>: </P>
              <N>320000000</N>
              <P>{" }"},</P>
            </Line>
            <Line indent={1}>
              <K>{q("putWall")}</K>
              <P>: {"{ "}</P>
              <K>{q("strike")}</K>
              <P>: </P>
              <N>615</N>
              <P>, </P>
              <K>{q("gex")}</K>
              <P>: </P>
              <N>-410000000</N>
              <P>{" }"},</P>
            </Line>
            <Line indent={1}>
              <K>{q("data")}</K>
              <P>: [</P>
            </Line>
            <Line indent={2}>
              <P>{"{ "}</P>
              <K>{q("strike")}</K>
              <P>: </P>
              <N>615</N>
              <P>, </P>
              <K>{q("netGEX")}</K>
              <P>: </P>
              <N>-410000000</N>
              <P>{" }"},</P>
            </Line>
            <Line indent={2}>
              <P>{"{ "}</P>
              <K>{q("strike")}</K>
              <P>: </P>
              <N>620</N>
              <P>, </P>
              <K>{q("netGEX")}</K>
              <P>: </P>
              <N>-46000000</N>
              <P>{" }"},</P>
            </Line>
            <Line indent={2}>
              <P>{"{ "}</P>
              <K>{q("strike")}</K>
              <P>: </P>
              <N>630</N>
              <P>, </P>
              <K>{q("netGEX")}</K>
              <P>: </P>
              <N>320000000</N>
              <P>{" }"}</P>
            </Line>
            <Line indent={1}>
              <P>],</P>
            </Line>
            <Line indent={1}>
              <K>{q("asOf")}</K>
              <P>: </P>
              <S>{q("2026-08-05T14:32:00Z")}</S>
            </Line>
            <Line>
              <P>{"}"}</P>
            </Line>
          </CodeWindow>
          <GexProfileMock />
        </div>
      </div>

      <StatsRow
        stats={[
          { value: "Live", label: "full-chain compute" },
          { value: "5", label: "GEX endpoints" },
          { value: "<60s", label: "data freshness" },
          { value: "All", label: "US optionable tickers" },
        ]}
      />

      {/* Features */}
      <div className="mt-24 sm:mt-28">
        <SectionHeading
          title={
            <>
              Dealer positioning, <span style={metalText}>fully mapped</span>
            </>
          }
          sub="One GET for the headline levels, deeper endpoints when you need the whole surface - across strikes, expirations and time."
        />
      </div>

      <FeatureSplit
        eyebrow="The whole surface"
        title={
          <>
            See where hedging <span style={metalText}>concentrates</span>
          </>
        }
        lead="Break net GEX down by strike and expiration to separate 0DTE pinning from monthly opex magnets - and pull vanna and charm from the same engine."
        points={[
          { term: "/v1/gex/heatmap", text: "net GEX per strike per expiration, one call" },
          { term: "/v1/vanna", text: "how dealer hedging shifts with vol and time decay" },
          { term: "expirations=", text: "narrow the compute to the expiries you trade" },
        ]}
        visual={<GexHeatmapMock />}
      />

      <FeatureSplit
        flip
        eyebrow="Regime change, streamed"
        title={
          <>
            Alerts on the flip, <span style={metalText}>not after it</span>
          </>
        }
        lead="A dedicated events endpoint surfaces gamma flips and wall migrations as they happen - so your bots react to regime change instead of discovering it in the P&L."
        points={[
          { term: "/v1/gex/events", text: "flip and wall-move events with timestamps" },
          { term: "/v1/gex/history", text: "replay how the walls evolved, for backtests" },
          { term: "formula=", text: "switch GEX formulas - the same knobs as the dashboard" },
        ]}
        visual={<GexEventsMock />}
      />

      {/* Endpoints */}
      <div className="mt-20 sm:mt-24">
        <SectionHeading
          title="The GEX surface, endpoint by endpoint"
          sub="Everything below is included with any paid plan and documented with runnable examples."
        />
        <EndpointTable
          endpoints={[
            { method: "GET", path: "/v1/gex", description: "Live GEX profile: walls, flip, per-strike data" },
            { method: "GET", path: "/v1/gex/history", description: "Historical snapshots for backtesting" },
            { method: "GET", path: "/v1/gex/heatmap", description: "Net GEX by strike and expiration" },
            { method: "GET", path: "/v1/gex/events", description: "Gamma flip & wall-move event stream" },
            { method: "GET", path: "/v1/vanna", description: "Vanna & charm exposure profile" },
            { method: "GET", path: "/v1/expirations", description: "Available expirations per symbol" },
          ]}
        />
        <p className="mt-5 text-center text-sm text-white/50">
          <Link href="/docs" className="inline-flex items-center gap-1.5 text-[#7fe7c8] underline-offset-4 hover:underline">
            Full GEX API reference
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </p>
      </div>

      {/* How it works */}
      <StepsCard
        title="From zero to gamma levels in three steps"
        steps={[
          {
            title: "Pick a plan",
            description: "Every paid QuantWheel plan includes the full GEX API.",
          },
          {
            title: "Generate your key",
            description: (
              <>
                Mint a key in{" "}
                <Link href="/dashboard/api" className="text-[#7fe7c8] underline-offset-4 hover:underline">
                  Dashboard &rarr; API
                </Link>{" "}
                and keep it secret.
              </>
            ),
          },
          {
            title: "Query any ticker",
            description: "GET /v1/gex?symbol=SPY with your Bearer key returns live walls in milliseconds.",
          },
        ]}
      />

      <div className="mt-20 sm:mt-24">
        <TextFeatureList
          items={[
            {
              title: "Tunable compute",
              description:
                "Filter by expirations and delta range, or switch GEX formulas - the same knobs as the QuantWheel GEX dashboard, exposed as query params.",
            },
            {
              title: "History included",
              description:
                "Historical GEX snapshots ship with every plan - validate a strategy against how walls and flips actually moved before wiring it to live data.",
            },
          ]}
        />
      </div>

      <CrossSellBand
        badge="No code? No problem"
        title="Ask your AI for the call wall instead"
        description="The same GEX engine ships as an MCP server - connect it to Claude or Cursor and ask for walls, flips and setups in plain English."
        href="/gex-mcp"
        linkLabel="Explore the GEX MCP"
      />

      <CrossSellBand
        className="mt-5"
        badge="Trade on TradingView?"
        title="GEX levels drawn straight on your charts"
        description="The QuantWheel desktop bridge overlays these exact levels on TradingView in real time - powered by the same API key."
        href="/tradingview-gex-bridge"
        linkLabel="See the TradingView bridge"
      />

      <FinalCta
        title="Know where the walls are, before the market tests them"
        sub="One key unlocks real-time gamma exposure for every US optionable ticker."
        keyHref={keyHref}
        eventName="gex_api_lp_get_key_clicked"
        note="Included with every paid QuantWheel plan."
      />
    </DevLandingShell>
  );
}
